import {Localize} from "@dp-instant/i18n";
import {Action, lazyGet} from "@dp-instant/core";
import {
    IEventEmitter,
    IVibrationManager,
    TypesCore,
    TypesSocial,
    TypesUI
} from "@dp-instant/interfaces";
import {v4 as uuidv4} from "uuid";
import {ISocial, ScoreType} from "@dp-instant/social";
import {UIEvents, useInjection, visibilityEffect, useEventListener} from "@dp-instant/ui";
import {GameModel} from "@game/coregame/model/GameModel";
import {TypesGame} from "@game/TypesGame";
import {Boosters} from "@game/ui/components/Booster/Boosters";
import {Capturing} from "@game/ui/components/Capturing/Capturing";
import {InviteButton} from "@game/ui/components/InviteButton/InviteButton";
import {LeaderboardButton} from "@game/ui/components/LeaderBoardButton/LeaderboardButton";
import {LevelTitle} from "@game/ui/components/LevelTitle/LevelTitle";
import {CoinsIndicator} from "@game/ui/components/CoinsIndicator/CoinsIndicator";
import {NoAdsButton} from "@game/ui/components/NoAdsButton/NoAdsButton";
import {SettingsButton} from "@game/ui/components/SetttingsButton/SettingsButton";
import {ShopButton} from "@game/ui/components/ShopButton/ShopButton";
import {TapToPlayButton} from "@game/ui/components/TapToPlayButton/TapToPlayButton";
import cn from "classnames";
import {useEffect, useCallback} from "preact/hooks";
import {PopupType} from "@game/ui/PopupType";
import "./StartScreen.less";
import {SkinManager} from "@game/coregame/managers/SkinManager";
import {playUIClickSound} from "@game/helpers";
import {UserStatusInfo} from "@game/ui/components/UserStatusInfo/UserStatusInfo";
import {useState} from "preact/hooks";
import {CookieModel} from "@game/coregame/model/CookieModel";
import {SIOConstants} from "@game/constants";
import {Playdeck} from "@game/coregame/utils/Playdeck";
import {apiClient} from "@game/api/ApiClient";
import {StarsIndicator} from "@game/ui/components/StarsIndicator/StarsIndicator";
import {setShopTab} from "@game/ui/components/Shop/ShopMenu";
import {ShopType} from "@game/ui/constants";

let offlineEarningsPopupShown = false;

export const StartScreen = () => {
    const model = useInjection<GameModel>(TypesGame.model);
    const social: ISocial & {userAuthorized?: boolean; authorizeUser?: () => Promise<boolean>} =
        useInjection<ISocial>(TypesSocial.model);
    const skinManager = useInjection<SkinManager>(TypesGame.skinManager);
    const dispatcher = useInjection<IEventEmitter>(TypesCore.dispatcher);

    const cookie = useInjection<CookieModel & {syncAfterAuthStateChange?: () => Promise<boolean>}>(
        TypesGame.cookieModel
    );

    const vibrationManager = lazyGet<IVibrationManager | undefined>(TypesSocial.vibrationManager);

    const [, forceRerender] = useState({});
    useEffect(() => {
        const onLocale = () => forceRerender({});
        dispatcher.on("LOCALE_CHANGED", onLocale);
        return () => { dispatcher.off("LOCALE_CHANGED", onLocale); };
    }, []);

    const {IN_GAME_AUDIO_CONTROL_ALLOWED} = SIOConstants;
    const SHOW_SETTINGS_BUTTON =
        IN_GAME_AUDIO_CONTROL_ALLOWED || (vibrationManager && vibrationManager.supported);

    const initialState = {
        authorized: social.userAuthorized === undefined ? true : social.userAuthorized,
        stageLevel: model.currentContinent.stageLevel,
        totalStages: model.currentContinent.totalStages
    };

    const [state, setState] = useState(() => initialState);
    const [showVipButton, setShowVipButton] = useState<boolean>(model.cookie.getVip);

    const [invisible, setInvisible] = visibilityEffect();

    const showShopNotification: boolean =
        [
            ...skinManager.availableBuildings,
            ...skinManager.availableFighters,
            ...model.cookie.availableColors
        ].filter(item => !item.stored).length > 0 && !model.cookie.getVip;

    const getOfflineEarningsTimeHelper = (
        date: Date
    ): {days: number; hours: number; minutes: number; seconds: number} => {
        let days = 0;
        let hours = 0;
        let minutes = 0;
        let seconds = 0;
        if (__SOCIAL__ === "yt" || __SOCIAL__ === "ls") {
            const timeDiff = cookie.timeDiff;
            days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
            hours = Math.floor((timeDiff / (1000 * 60 * 60)) % 24);
            minutes = Math.floor((timeDiff / (1000 * 60)) % 60);
            seconds = Math.floor((timeDiff / 1000) % 60);
        } else {
            hours = date.getUTCHours();
            minutes = date.getUTCMinutes();
            seconds = date.getUTCSeconds();
        }

        return {days, hours, minutes, seconds};
    };

    const tryToShowOfflineEarningsPopup = () => {
        const offlineEarning = model.getOfflineEarning();
        if (offlineEarning.isRewarded) {
            const {days, hours, minutes, seconds} = getOfflineEarningsTimeHelper(
                offlineEarning.date
            );
            const coins = offlineEarning.reward;
            dispatcher.emit(UIEvents.POPUP, {
                id: PopupType.OFFLINE_EARNINGS,
                props: {
                    coins,
                    hours,
                    minutes,
                    seconds,
                    days
                }
            });
        }
    };

    useEffect(() => {
        console.log();
        if (!offlineEarningsPopupShown && SIOConstants.OFFLINE_EARNINGS_ALLOWED) {
            tryToShowOfflineEarningsPopup();
            offlineEarningsPopupShown = true;
        }
        // Show daily leaderboard reward popup if pending
        if (__SOCIAL__ === "tg") {
            const pending = (window as any).__tgPendingDailyReward;
            if (pending) {
                (window as any).__tgPendingDailyReward = null;
                setTimeout(() => {
                    dispatcher.emit(UIEvents.POPUP, {
                        id: PopupType.DAILY_LEADERBOARD_REWARD,
                        props: {
                            place: pending.place,
                            reward: pending.reward,
                            onConfirm: () => {
                                apiClient.claimDailyLeaderboardReward().catch(() => {});
                            }
                        }
                    });
                }, 500);
            }
        }
    }, [offlineEarningsPopupShown]);

    // Auto-start first battle for new TG players (skip menu)
    useEffect(() => {
        if (__SOCIAL__ === "tg" && model.cookie.absoluteTryNum === 0) {
            setInvisible(true);
            apiClient.trackFunnelEvent("first_battle_start");
            lazyGet<Action>(TypesGame.actions.startGame)?.run();
        }
    }, []);

    return (
        <div className={cn("screen", "screen__start")}>
            {__SOCIAL__ === "tg" && (
                <div className="level-number-tg-header">
                    <LevelTitle />
                </div>
            )}
            <div className={cn("screen-top", {invisible})}>
                <div className={cn("container", "top-bar")}>
                    {SHOW_SETTINGS_BUTTON ? (
                        <SettingsButton
                            onClick={() =>
                                dispatcher.emit(UIEvents.POPUP, {id: PopupType.SETTINGS})
                            }
                        />
                    ) : (
                        <div className={cn("stub", "button", "settings-button")}></div>
                    )}
                    {__SOCIAL__ !== "tg" && <LevelTitle />}
                    <div className="top-bar__currencies" onClick={() => {
                        playUIClickSound();
                        setShopTab(ShopType.STARS);
                        dispatcher.emit(UIEvents.SCREEN_CHANGED, {
                            id: TypesUI.screen.SHOP,
                            props: {}
                        });
                    }}>
                        <CoinsIndicator
                            total={model.cookie.coins}
                            className={cn("coins-indicator", "coins-indicator_filled")}
                        />
                        {__SOCIAL__ === "tg" && (
                            <StarsIndicator
                                className={cn("stars-indicator", "stars-indicator_filled")}
                            />
                        )}
                    </div>
                </div>
                <div className={cn("container", "buttons-container", {invisible})}>
                    <span className={cn("buttons-group", "buttons-group_left")}>
                        {!showVipButton && (
                            <NoAdsButton
                                onClick={() => {
                                    playUIClickSound();
                                    if (__SOCIAL__ === "tg") {
                                        dispatcher.emit(UIEvents.POPUP, {
                                            id: PopupType.NO_ADS_PURCHASE,
                                            props: {
                                                onConfirm: (confirmed?: boolean) => {
                                                    if (confirmed) {
                                                        model.cookie.setVip = true;
                                                        setShowVipButton(true);
                                                    }
                                                }
                                            }
                                        });
                                    } else {
                                        Playdeck.requestPayment("no ads", 499, uuidv4()).then(result => {
                                            if (result.status === "paid") {
                                                model.cookie.setVip = true;
                                                setShowVipButton(true);
                                            }
                                        });
                                    }
                                }}
                            />
                        )}
                        {["ya", "gd", "yt", "ls", "pd"].includes(social.socialPlatform) ? null : (
                            <InviteButton />
                        )}
                        {["gd", "yt", "ls", "pd"].includes(social.socialPlatform) ? null : (
                            <LeaderboardButton
                                onClick={async () => {
                                    if (__SOCIAL__ === "tg") {
                                        try {
                                            const lb = await apiClient.getLeaderboard("global");
                                            const users = lb.entries.map(e => ({
                                                id: e.telegramId,
                                                name: e.firstName,
                                                photo: e.photoUrl || "",
                                                score: e.level,
                                                scoreGlobal: e.level,
                                                scoreContext: e.level,
                                                scores: { getScore: () => e.level }
                                            }));
                                            dispatcher.emit(UIEvents.SCREEN_CHANGED, {
                                                id: TypesUI.screen.LEADERBOARD,
                                                props: {
                                                    scoreType: ScoreType.GLOBAL,
                                                    users
                                                }
                                            });
                                        } catch (err) {
                                            console.warn("[TG] Leaderboard load failed:", err);
                                        }
                                    } else {
                                        dispatcher.emit(UIEvents.SCREEN_CHANGED, {
                                            id: TypesUI.screen.LEADERBOARD,
                                            props: {
                                                scoreType: ScoreType.GLOBAL,
                                                users: social.friends.concat(social.me)
                                            }
                                        });
                                    }
                                }}
                            />
                        )}
                    </span>
                    <span className={cn("buttons-group", "buttons-group_right")}>
                        <ShopButton
                            showNotification={showShopNotification}
                            onClick={() => {
                                playUIClickSound();
                                dispatcher.emit(UIEvents.SCREEN_CHANGED, {
                                    id: TypesUI.screen.SHOP,
                                    props: {}
                                });
                            }}
                        />
                    </span>
                </div>
            </div>
            <TapToPlayButton
                onDown={() => setInvisible(true)}
                onClick={() => lazyGet<Action>(TypesGame.actions.startGame)?.run()}
            />
            {!invisible && (
                <Capturing
                    captured={state.stageLevel / state.totalStages}
                    title={__SOCIAL__ !== "tg" ? Localize.get("ui-menu-capturing", "CAPTURING") : undefined}
                    stages={state.totalStages}
                    showGift={social.inSolo}
                />
            )}
            <div className={cn("screen-bottom", {invisible})}>
                <Boosters />
            </div>
            <UserStatusInfo
                authorized={state.authorized}
                onLogin={async () => {
                    if (social.authorizeUser) {
                        const authorized = await social.authorizeUser();
                        if (authorized) {
                            if (cookie.syncAfterAuthStateChange) {
                                const synced = await cookie.syncAfterAuthStateChange();
                                if (synced) {
                                    await lazyGet<Action>(
                                        TypesGame.actions.levelRestartAfterYandexLoginAction
                                    )?.run();
                                    setState(state => ({
                                        ...state,
                                        authorized,
                                        stageLevel: model.currentContinent.stageLevel,
                                        totalStages: model.currentContinent.totalStages
                                    }));
                                }
                            }
                        }
                    }
                }}
            />
        </div>
    );
};
